import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, TextField, Button, Alert, MenuItem,
  FormControlLabel, Switch, Typography, CircularProgress, Paper, Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SearchableEntitySelect, EntityOption } from '../../components/common/SearchableEntitySelect';
import { BiltyItemsEditor, BiltyItemRow } from '../../components/bilty/BiltyItemsEditor';
import { DescriptionOption } from '../../components/bilty/CreatableDescriptionSelect';
import { biltyAPI, biltyItemDescriptionAPI, clientAPI, consignorAPI, consigneeAPI, vehicleAPI } from '../../services/api';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';

const toDateInput = (value?: string | Date) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  return new Date(value).toISOString().slice(0, 10);
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 0.5,
    pb: 1, borderBottom: `2px solid ${brandCss.green100}`,
  }}>
    <Box sx={{ color: brandCss.primary, display: 'flex' }}>{icon}</Box>
    <Typography fontSize={14} fontWeight={800} color={brandCss.primaryDark}>{title}</Typography>
  </Box>
);

const num = (v: unknown) => Number(v) || 0;

export const BiltyFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<EntityOption[]>([]);
  const [consignors, setConsignors] = useState<EntityOption[]>([]);
  const [consignees, setConsignees] = useState<EntityOption[]>([]);
  const [consigneeRecords, setConsigneeRecords] = useState<Record<string, string>[]>([]);
  const [vehicles, setVehicles] = useState<EntityOption[]>([]);
  const [itemDescriptions, setItemDescriptions] = useState<DescriptionOption[]>([]);
  const [manualNumber, setManualNumber] = useState(false);
  const [numberCheck, setNumberCheck] = useState('');
  const [form, setForm] = useState({
    biltyNumber: '',
    fiscalYear: '',
    date: toDateInput(),
    client: '',
    consignor: '',
    consignee: '',
    source: '',
    destination: '',
    vehicle: '',
    deliveryAddress: '',
    deliveryPhone: '',
    taxPercentage: '0',
    remarks: '',
    status: 'active',
    freight: '',
    doorCollection: '',
    doorDelivery: '',
    hamali: '',
    stCharge: '',
    advance: '',
    cnType: 'Billed',
    insuranceStatus: 'Uninsured',
    policyNo: '',
    insuranceAmount: '',
    items: [] as BiltyItemRow[],
  });

  useEffect(() => {
    Promise.all([
      clientAPI.getAll().then((r) => r.data),
      consignorAPI.getAll().then((r) => r.data),
      consigneeAPI.getAll().then((r) => r.data),
      vehicleAPI.getAll().then((r) => r.data),
      biltyItemDescriptionAPI.getAll({ status: 'active' }).then((r) => r.data),
    ]).then(([c, cn, ce, v, descriptions]) => {
      setClients(c.map((x: Record<string, string>) => ({
        _id: x._id,
        label: x.clientName || x.companyFullName,
        sublabel: x.companyFullName,
      })));
      setConsignors(cn.map((x: Record<string, string>) => ({
        _id: x._id,
        label: x.name,
        sublabel: x.city,
      })));
      setConsigneeRecords(ce);
      setConsignees(ce.map((x: Record<string, string>) => ({
        _id: x._id,
        label: x.name,
        sublabel: x.city,
      })));
      setVehicles(v.map((x: Record<string, string>) => ({
        _id: x._id,
        label: x.vehicleNumber,
        sublabel: [x.vehicleType, x.brand].filter(Boolean).join(' · '),
      })));
      setItemDescriptions((descriptions || []).map((d: Record<string, string>) => ({
        _id: d._id,
        name: d.name,
        category: d.category,
      })));
    }).catch(() => setError('Failed to load master data'));
  }, []);

  const handleCreateDescription = async (name: string): Promise<DescriptionOption | null> => {
    try {
      const res = await biltyItemDescriptionAPI.create({ name: name.trim(), status: 'active' });
      const created: DescriptionOption = {
        _id: res.data._id,
        name: res.data.name,
        category: res.data.category,
      };
      setItemDescriptions((prev) => {
        if (prev.some((d) => d.name.toLowerCase() === created.name.toLowerCase())) return prev;
        return [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      return created;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!isEdit || !id) return;
    biltyAPI.getById(id).then((res) => {
      const b = res.data.bilty;
      setManualNumber(b.numberMode === 'manual');
      setForm({
        biltyNumber: b.biltyNumber || '',
        fiscalYear: b.fiscalYear || '',
        date: toDateInput(b.date),
        client: b.client?._id || b.client || '',
        consignor: b.consignor?._id || b.consignor || '',
        consignee: b.consignee?._id || b.consignee || '',
        source: b.source || '',
        destination: b.destination || '',
        vehicle: b.vehicle?._id || b.vehicle || '',
        deliveryAddress: b.deliveryAddress || b.consigneeAddress || '',
        deliveryPhone: b.deliveryPhone || b.consignee?.mobile || '',
        taxPercentage: String(b.taxPercentage ?? 0),
        remarks: b.remarks || '',
        status: b.status || 'active',
        freight: String(b.freight ?? b.freightAmount ?? ''),
        doorCollection: String(b.doorCollection ?? ''),
        doorDelivery: String(b.doorDelivery ?? ''),
        hamali: String(b.hamali ?? ''),
        stCharge: String(b.stCharge ?? ''),
        advance: String(b.advance ?? ''),
        cnType: b.cnType || 'Billed',
        insuranceStatus: b.insuranceStatus || 'Uninsured',
        policyNo: b.policyNo || '',
        insuranceAmount: String(b.insuranceAmount ?? ''),
        items: (b.items || []).map((item: BiltyItemRow) => ({
          ...item,
          invoiceDate: item.invoiceDate ? toDateInput(String(item.invoiceDate)) : '',
          actualWeight: item.actualWeight ?? '',
          chargedWeight: item.chargedWeight ?? item.weight ?? '',
        })),
      });
    }).catch(() => setError('Failed to load bilty')).finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit || manualNumber) return;
    biltyAPI.getNextNumber(form.date).then((res) => {
      setForm((f) => ({
        ...f,
        biltyNumber: res.data.biltyNumber,
        fiscalYear: res.data.fiscalYear,
      }));
    }).catch(() => {});
  }, [form.date, isEdit, manualNumber]);

  useEffect(() => {
    if (!manualNumber || !form.biltyNumber) return;
    const timer = setTimeout(() => {
      biltyAPI.checkNumber(form.biltyNumber, id).then((res) => {
        setNumberCheck(res.data.available ? '' : 'Bilty number already exists');
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [form.biltyNumber, manualNumber, id]);

  const chargesTotal = useMemo(
    () => num(form.freight) + num(form.doorCollection) + num(form.doorDelivery) + num(form.hamali) + num(form.stCharge),
    [form.freight, form.doorCollection, form.doorDelivery, form.hamali, form.stCharge]
  );

  const taxAmount = useMemo(
    () => Math.round(chargesTotal * num(form.taxPercentage) / 100 * 100) / 100,
    [chargesTotal, form.taxPercentage]
  );

  const grandTotal = useMemo(() => Math.round((chargesTotal + taxAmount) * 100) / 100, [chargesTotal, taxAmount]);
  const balance = useMemo(() => Math.round((grandTotal - num(form.advance)) * 100) / 100, [grandTotal, form.advance]);

  const handleConsigneeChange = (consigneeId: string) => {
    const doc = consigneeRecords.find((c) => String(c._id) === consigneeId);
    setForm((f) => ({
      ...f,
      consignee: consigneeId,
      deliveryAddress: doc
        ? [doc.address, doc.city, doc.state].filter(Boolean).join(', ')
        : f.deliveryAddress,
      deliveryPhone: doc?.mobile || f.deliveryPhone,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (!form.source || !form.destination) {
        setError('From and To are required');
        return;
      }
      if (!form.consignor || !form.consignee) {
        setError('Please select sender and receiver under Billing Invoice');
        return;
      }
      if (!form.items.some((item) => String(item.description || '').trim())) {
        setError('Add at least one item with description');
        return;
      }
      if (manualNumber && numberCheck) {
        setError(numberCheck);
        return;
      }
      const payload = {
        ...form,
        numberMode: manualNumber ? 'manual' : 'auto',
        taxPercentage: num(form.taxPercentage),
        freight: num(form.freight),
        doorCollection: num(form.doorCollection),
        doorDelivery: num(form.doorDelivery),
        hamali: num(form.hamali),
        stCharge: num(form.stCharge),
        advance: num(form.advance),
        insuranceAmount: num(form.insuranceAmount),
        client: form.client || undefined,
        consignor: form.consignor || undefined,
        consignee: form.consignee || undefined,
        vehicle: form.vehicle || undefined,
        items: form.items.filter((item) => String(item.description || '').trim()),
        freightAmount: chargesTotal,
      };
      if (isEdit && id) {
        await biltyAPI.update(id, payload);
      } else {
        await biltyAPI.create(payload);
      }
      navigate('/bilty');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Edit Transport Bilty' : 'Add Transport Bilty'}
        subtitle={getPageSubtitle('bilty_form')}

        action={
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/bilty')}>Bilty List</Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: `${RADIUS}px`, mb: 2, border: `1px solid ${brandColors.border}` }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={manualNumber} onChange={(e) => setManualNumber(e.target.checked)} />}
              label="Manual bilty number"
            />
          </Box>

          <SectionHeader icon={<LocalShippingOutlinedIcon fontSize="small" />} title="Lorry Info" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <SearchableEntitySelect
                label="Lorry No."
                placeholder="Search vehicle..."
                value={form.vehicle}
                options={vehicles}
                onChange={(v) => setForm({ ...form, vehicle: v })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField label="Bilty No." value={form.biltyNumber}
                onChange={(e) => setForm({ ...form, biltyNumber: e.target.value })}
                disabled={!manualNumber && !isEdit}
                fullWidth size="small" sx={catchyTextFieldSx}
                error={Boolean(numberCheck)} helperText={numberCheck || `FY: ${form.fiscalYear || '—'}`} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField label="Date" type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField label="From" placeholder="Source city" value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                required fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField label="To" placeholder="Destination city" value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })}
                required fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
          </Grid>

          <SectionHeader icon={<ReceiptLongOutlinedIcon fontSize="small" />} title="Billing Invoice" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <SearchableEntitySelect label="Sender" placeholder="Search sender..."
                value={form.consignor} options={consignors}
                onChange={(v) => setForm({ ...form, consignor: v })} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableEntitySelect label="Receiver" placeholder="Search receiver..."
                value={form.consignee} options={consignees}
                onChange={handleConsigneeChange} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableEntitySelect label="Client (optional)" placeholder="Search client..."
                value={form.client} options={clients}
                onChange={(v) => setForm({ ...form, client: v })} />
            </Grid>
          </Grid>

          <SectionHeader icon={<PlaceOutlinedIcon fontSize="small" />} title="Delivery Info" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField label="Delivery Address" placeholder="Enter delivery address"
                value={form.deliveryAddress} multiline minRows={2}
                onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Phone" placeholder="Delivery phone"
                value={form.deliveryPhone}
                onChange={(e) => setForm({ ...form, deliveryPhone: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <BiltyItemsEditor
              items={form.items}
              onChange={(items) => setForm({ ...form, items })}
              descriptions={itemDescriptions}
              onCreateDescription={handleCreateDescription}
            />
          </Box>

          <SectionHeader icon={<PaymentsOutlinedIcon fontSize="small" />} title="Charges" />
          <Paper elevation={0} sx={{ border: `1px solid ${brandColors.border}`, borderRadius: `${RADIUS}px`, p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Freight" type="number" value={form.freight}
                  onChange={(e) => setForm({ ...form, freight: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Door Collection" type="number" value={form.doorCollection}
                  onChange={(e) => setForm({ ...form, doorCollection: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Door Delivery" type="number" value={form.doorDelivery}
                  onChange={(e) => setForm({ ...form, doorDelivery: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Hamali" type="number" value={form.hamali}
                  onChange={(e) => setForm({ ...form, hamali: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="STCH" type="number" value={form.stCharge}
                  onChange={(e) => setForm({ ...form, stCharge: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Total" value={chargesTotal.toFixed(2)} disabled
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Advance" type="number" value={form.advance}
                  onChange={(e) => setForm({ ...form, advance: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Balance" value={balance.toFixed(2)} disabled
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField select label="CN Type" value={form.cnType}
                  onChange={(e) => setForm({ ...form, cnType: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx}>
                  <MenuItem value="Billed">Billed</MenuItem>
                  <MenuItem value="To Pay">To Pay</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField select label="Insurance Status" value={form.insuranceStatus}
                  onChange={(e) => setForm({ ...form, insuranceStatus: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx}>
                  <MenuItem value="Uninsured">Uninsured</MenuItem>
                  <MenuItem value="Insured">Insured</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Policy No." value={form.policyNo}
                  onChange={(e) => setForm({ ...form, policyNo: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Insurance Amount" type="number" value={form.insuranceAmount}
                  onChange={(e) => setForm({ ...form, insuranceAmount: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField label="Tax %" type="number" value={form.taxPercentage}
                  onChange={(e) => setForm({ ...form, taxPercentage: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Remarks" value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField select label="Status" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  fullWidth size="small" sx={catchyTextFieldSx}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
              Grand Total (incl. tax): {grandTotal.toFixed(2)}
            </Typography>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              sx={{ ...catchyPrimaryButtonSx, px: 4, py: 1.2, background: brandCss.gradientPrimary }}>
              {isEdit ? 'Update Bilty' : 'Add Bilty'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
